/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { EventProxy } from "../../core/event-proxy";
import { EdgeSet } from "../../math/edge-set";
import { degToRad } from "../../math/float";
import { Vertex3D } from "../../math/vertex3d";
import { CollisionType } from "../../physics/collision-type";
import type { HitObject } from "../../physics/hit-object";
import { HitPoint } from "../../physics/hit-point";
import { HitTriangle } from "../../physics/hit-triangle";
import type { Table } from "../../table-model";
import type { HitTargetData } from "./hit-target-data";

/**
 * Half thickness of a target in units of `vSize.y`, and its height in units of `vSize.z`.
 * Upstream reads a mesh file for the target. Here the hit shape is a box that the
 * code builds from `position`, `vSize` and `rotZ`.
 */
const HALF_THICKNESS = 0.1;
const HEIGHT = 1.4;

/** The six faces of the box as corner indices. A corner index is `ix + 2 * iy + 4 * iz`. */
const FACES: readonly (readonly [number, number, number, number])[] = [
  [0, 1, 3, 2], // bottom, z low
  [4, 6, 7, 5], // top, z high
  [0, 4, 5, 1], // back, y low
  [2, 3, 7, 6], // front, y high
  [0, 2, 6, 4], // x low
  [1, 5, 7, 3], // x high
];

/** The index of the front face (the +y face). Only this face fires hit events. */
const FRONT_FACE = 3;

interface Tri {
  readonly a: number;
  readonly b: number;
  readonly c: number;
}

export class HitTargetHitGenerator {
  private readonly data: HitTargetData;

  constructor(data: HitTargetData) {
    this.data = data;
  }

  public generateHitObjects(events: EventProxy, table: Table): HitObject[] {
    const corners = this.getCorners(table);
    const center = new Vertex3D(0, 0, 0);
    for (const c of corners) {
      center.add(c);
    }
    center.divideScalar(corners.length);

    // Collect the triangles of every face and fix their winding so that the normal points out.
    const faceTris: Tri[][] = FACES.map((face) => {
      const [f0, f1, f2, f3] = face;
      return [this.orient(corners, center, f0, f1, f2), this.orient(corners, center, f0, f2, f3)];
    });

    const hitObjects: HitObject[] = [];
    const addedEdges = new EdgeSet();

    // The whole box is solid but fires no event.
    for (const tris of faceTris) {
      for (const tri of tris) {
        hitObjects.push(...this.generateTriangle(corners, tri, addedEdges, events, table, false));
      }
    }
    for (const corner of corners) {
      hitObjects.push(this.setupHitObject(new HitPoint(corner.clone()), events, false, table));
    }

    // A second shape lies on the front face. It fires the hit event. A ball that hits the
    // target from behind or from the side does not fire an event.
    const frontEdges = new EdgeSet();
    for (const tri of faceTris[FRONT_FACE]!) {
      hitObjects.push(...this.generateTriangle(corners, tri, frontEdges, events, table, true));
    }
    for (const index of FACES[FRONT_FACE]!) {
      hitObjects.push(
        this.setupHitObject(new HitPoint(corners[index]!.clone()), events, true, table),
      );
    }
    return hitObjects;
  }

  /**
   * The eight corners of the box in table coordinates.
   * Local x is the width, local y points to the front, local z is up.
   */
  private getCorners(table: Table): Vertex3D[] {
    const rot = degToRad(this.data.rotZ);
    const sin = Math.sin(rot);
    const cos = Math.cos(rot);
    const size = this.data.vSize;
    const baseZ = this.data.position.z + table.getTableHeight();
    const corners: Vertex3D[] = [];
    for (let i = 0; i < 8; i++) {
      const lx = ((i & 1) === 0 ? -0.5 : 0.5) * size.x;
      const ly = ((i & 2) === 0 ? -HALF_THICKNESS : HALF_THICKNESS) * size.y;
      const lz = (i & 4) === 0 ? 0 : HEIGHT * size.z * table.getScaleZ();
      corners.push(
        new Vertex3D(
          this.data.position.x + lx * cos - ly * sin,
          this.data.position.y + lx * sin + ly * cos,
          baseZ + lz,
        ),
      );
    }
    return corners;
  }

  /** Returns the triangle (a, b, c) or (a, c, b), the one whose hit normal points away from `center`. */
  private orient(corners: Vertex3D[], center: Vertex3D, a: number, b: number, c: number): Tri {
    const va = corners[a]!;
    const vb = corners[b]!;
    const vc = corners[c]!;
    // same formula as HitTriangle: normal = (v2 - v0) x (v1 - v0)
    const e0 = vc.clone().sub(va);
    const e1 = vb.clone().sub(va);
    const normal = Vertex3D.crossProduct(e0, e1);
    const mid = va.clone().add(vb).add(vc).divideScalar(3);
    const outward = normal.dot(mid.sub(center));
    return outward >= 0 ? { a, b, c } : { a, b: c, c: b };
  }

  private generateTriangle(
    corners: Vertex3D[],
    tri: Tri,
    addedEdges: EdgeSet,
    events: EventProxy,
    table: Table,
    setHitObject: boolean,
  ): HitObject[] {
    const rgv3D = [corners[tri.a]!.clone(), corners[tri.b]!.clone(), corners[tri.c]!.clone()];
    const result: HitObject[] = [
      this.setupHitObject(new HitTriangle(rgv3D), events, setHitObject, table),
    ];
    const edges: [number, number, Vertex3D, Vertex3D][] = [
      [tri.a, tri.c, rgv3D[0]!, rgv3D[2]!],
      [tri.c, tri.b, rgv3D[2]!, rgv3D[1]!],
      [tri.b, tri.a, rgv3D[1]!, rgv3D[0]!],
    ];
    for (const [i, j, vi, vj] of edges) {
      for (const line of addedEdges.addHitEdge(i, j, vi, vj)) {
        result.push(this.setupHitObject(line, events, setHitObject, table));
      }
    }
    return result;
  }

  private setupHitObject(
    obj: HitObject,
    events: EventProxy,
    setHitObject: boolean,
    table: Table,
  ): HitObject {
    obj.applyPhysics(this.data, table);
    obj.threshold = this.data.threshold;
    obj.setType(CollisionType.HitTarget);
    obj.obj = events;
    obj.fe = setHitObject && this.data.useHitEvent;
    return obj;
  }
}
